"use client";

import { cn } from "@/lib/utils";
import { ReactNode } from "react";

interface NeonCardProps {
  children: ReactNode;
  className?: string;
  glow?: "cyan" | "magenta" | "purple" | "green" | "yellow" | "none";
  hover?: boolean;
  onClick?: () => void;
}

const glowColors = {
  cyan: "shadow-[0_0_20px_rgba(0,240,255,0.15)]",
  magenta: "shadow-[0_0_20px_rgba(255,0,170,0.15)]",
  purple: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  green: "shadow-[0_0_20px_rgba(0,255,102,0.15)]",
  yellow: "shadow-[0_0_20px_rgba(255,200,0,0.15)]",
  none: "",
};

const borderColors = {
  cyan: "border-cyan-500/20 hover:border-cyan-500/40",
  magenta: "border-purple-500/20 hover:border-purple-500/40",
  purple: "border-purple-500/20 hover:border-purple-500/40",
  green: "border-green-500/20 hover:border-green-500/40",
  yellow: "border-yellow-500/20 hover:border-yellow-500/40",
  none: "border-[rgb(var(--border))]/60 hover:border-[rgb(var(--border))]/60",
};

export function NeonCard({
  children,
  className,
  glow = "cyan",
  hover = true,
  onClick,
}: NeonCardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        // Base glass effect
        "relative rounded-xl border",
        "bg-white/[0.04]",
        // Glow and border
        glowColors[glow],
        borderColors[glow],
        // Transitions
        "transition-all duration-300",
        // Hover effects
        hover && "cursor-pointer hover:translate-y-[-2px]",
        className
      )}
    >
      {/* Subtle top accent line */}
      {glow !== "none" && (
        <div
          className={cn(
            "absolute inset-x-0 top-0 h-px",
            glow === "cyan" && "bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent",
            glow === "magenta" && "bg-gradient-to-r from-transparent via-purple-500/30 to-transparent",
            glow === "purple" && "bg-gradient-to-r from-transparent via-purple-500/30 to-transparent",
            glow === "green" && "bg-gradient-to-r from-transparent via-green-500/30 to-transparent",
            glow === "yellow" && "bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent"
          )}
        />
      )}
      {children}
    </div>
  );
}
